import { Component, computed, inject, signal, OnInit } from "@angular/core";
import { EducationLevelGroup } from "@domain/EducationLevel";
import { RewardCursor } from "@domain/User";
import { RdxDialogContentDirective, RdxDialogTitleDirective, RdxDialogCloseDirective, injectDialogData } from "@radix-ng/primitives/dialog";
import { AnalyticsService, RewardCursorSelectionSource } from "@services/analytics.service";
import { RewardService } from "@services/reward.service";
import { AuthState } from "@states/auth.state";

type RewardCursorProgression = {
  cursor: RewardCursor;
  isUnlocked: boolean;
  isSelected: boolean;
}[];

@Component({
  selector: "app-cursor-selection-modal",
  standalone: true,
  imports: [RdxDialogContentDirective, RdxDialogTitleDirective, RdxDialogCloseDirective],
  template: `
    <div class="modal modal-medium" data-testid="cursor-selection-modal" aria-labelledby="cursor-selection-modal-title" rdxDialogContent>
      <div class="modal-content cursor-selection-modal__content">
        <h3 id="cursor-selection-modal-title" class="title-h3 cursor-selection-modal__title" rdxDialogTitle>Modifie ton curseur</h3>

        <div class="cursor-selection-modal__icons">
          @for (rewardCursorProgression of rewardCursorProgressionOrder(); track rewardCursorProgression.cursor) {
            <div
              class="cursor-selection-modal__cursor"
              [class.selected]="rewardCursorProgression.isSelected"
              [class.disabled]="!rewardCursorProgression.isUnlocked"
            >
              <input
                type="radio"
                name="cursor-selection"
                [id]="'cursor-selection-' + rewardCursorProgression.cursor"
                [value]="rewardCursorProgression.cursor"
                class="cursor-selection-modal__checkbox"
                [attr.checked]="rewardCursorProgression.isSelected ? true : null"
                [disabled]="!rewardCursorProgression.isUnlocked"
                (change)="handleCursorSelection(rewardCursorProgression.cursor)"
              />
              <label
                [htmlFor]="'cursor-selection-' + rewardCursorProgression.cursor"
                class="cursor-icon"
                [attr.data-testid]="'cursor-icon-' + rewardCursorProgression.cursor"
              >
                <img class="cursor-icon__arrow" src="assets/cursors/cursor-arrow.svg" alt="Cursor Arrow" />
                <img
                  class="cursor-icon__image"
                  [src]="'assets/cursors/' + rewardCursorProgression.cursor + '.svg'"
                  [alt]="'Sélectionne le curseur ' + rewardCursorProgression.cursor"
                />
              </label>
            </div>
          }
        </div>
      </div>

      <div class="modal-actions">
        <button class="btn btn-secondary" rdxDialogClose>Annuler</button>
        <button class="btn btn-primary" (click)="handleConfirm()" rdxDialogClose>Confirmer</button>
      </div>
    </div>
  `,
  styles: `
    @use "responsive" as *;
    @use "visuallyhidden" as *;

    .cursor-selection-modal__content {
      display: flex;
      padding: 40px 32px;
      flex-direction: column;
      align-items: center;
      gap: 32px;
      align-self: stretch;
    }

    .cursor-selection-modal__title {
      text-align: center;
    }

    .cursor-selection-modal__icons {
      display: grid;
      grid-template-columns: repeat(4, auto);
      gap: 32px;
      justify-content: center;
    }

    .cursor-icon {
      position: relative;
      display: flex;
      width: 105px;
      height: 105px;
      padding: 8px 16px;
      justify-content: center;
      align-items: center;
      gap: 8px;
      border-radius: 20px;
      border: 2px solid #d1dde0;
      background: #fff;
      box-shadow: 0 4px 0 0 #d1dde0;
      cursor: pointer;
      transition: all 0.2s ease;

      &:focus {
        outline: 2px solid #007bff;
        outline-offset: 2px;
      }
    }

    .cursor-icon__arrow {
      position: absolute;
      top: 4px;
      left: 4px;
      width: 20px;
      height: 29px;
    }

    .cursor-icon__image {
      display: block;
      width: 48px;
      height: 48px;
    }

    .cursor-selection-modal__cursor {
      &.selected .cursor-icon {
        box-shadow:
          0 0 0 2px #fff,
          0 0 0 6px #2e7b84,
          0 4px 0 0 #d1dde0;
      }

      &.disabled {
        pointer-events: none;

        .cursor-icon__arrow,
        .cursor-icon__image {
          filter: grayscale(1) opacity(0.4);
        }
      }
    }

    .cursor-selection-modal__checkbox {
      @include visuallyhidden();
      position: relative;
    }
  `,
})
export class CursorSelectionModal implements OnInit {
  private rewardService = inject(RewardService);
  private authState = inject(AuthState);
  private analyticsService = inject(AnalyticsService);

  private currentCursor = computed(() => this.authState.currentUser()?.rewardCursors.getCurrentCursor());

  protected selectedCursor = signal<RewardCursor | undefined>(undefined);
  private initialCurrentCursor = signal<RewardCursor | undefined>(undefined);

  readonly data = injectDialogData<{
    selectedEducationLevelGroup: EducationLevelGroup;
  }>();

  ngOnInit() {
    this.initialCurrentCursor.set(this.authState.currentUser()?.rewardCursors.getCurrentCursor());
  }
  protected rewardCursorProgressionOrder = computed<RewardCursorProgression>(() => {
    const currentUser = this.authState.currentUser();
    if (!currentUser) return [];

    const selected = this.selectedCursor() ?? this.currentCursor();
    return currentUser.rewardCursors.getCursorProgressionWithStatus().map(item => ({
      ...item,
      isSelected: item.cursor === selected,
    }));
  });

  protected handleCursorSelection(cursor: RewardCursor) {
    this.selectedCursor.set(cursor);
  }

  protected handleConfirm() {
    const cursor = this.selectedCursor() ?? this.currentCursor();
    if (cursor) {
      this.rewardService.selectCursorByName(cursor);

      if (cursor !== this.initialCurrentCursor()) {
        this.analyticsService.trackRewardCursorSelection(this.data.selectedEducationLevelGroup, cursor, RewardCursorSelectionSource.SelectionModal);
      }
    }
  }
}
